import React, { useState, useMemo } from 'react';
import { PlanogramComplianceBox, OosDetectionState, ComplianceStatus, RogItemStatus } from '../../../types';
import { BoxedImage, Crop } from '../../../components/BoxedImage';

interface PlanogramComplianceActionContentProps {
  item: PlanogramComplianceBox;
  cameraImageElement?: HTMLImageElement;
  layerBboxColor?: string;
}

export const PlanogramComplianceActionContent: React.FC<
  PlanogramComplianceActionContentProps
> = ({ item, cameraImageElement, layerBboxColor }) => {
  const [zoomedImage, setZoomedImage] = useState<HTMLImageElement | null>(null);

  // Use the provided HTMLImageElement directly
  const cropImage = cameraImageElement;

  // Use pre-calculated crop coordinates from item.shape
  // Use template coordinates for 'low', otherwise normal
  const cropCoordinates = useMemo((): Crop | undefined => {
    if (!cropImage) return undefined;
    if (item.osaStatus === OosDetectionState.LOW && item.templateCoordinates) {
      return item.shape || undefined;
    }
    return item.shape || undefined;
  }, [item.shape, item.templateCoordinates, item.osaStatus, cropImage]);

  return (
    <>
      {/* First Row: Crop Image + Details */}
      <div className="flex gap-4 mb-4">
        {/* Left: Cropped Camera Image */}
        <div className="flex-shrink-0 w-48">
          {/* Show planogram reference for OUT items (not FACEOVER) OR faceovers without coordinates */}
          {((item.osaStatus === OosDetectionState.OUT && item.complianceStatus !== ComplianceStatus.FACEOVER) ||
            (item.complianceStatus === ComplianceStatus.FACEOVER && (item.x1 == null || item.y1 == null || item.x2 == null || item.y2 == null))) &&
          item.productImageUri ? (
            <div>
              <span className="text-gray-600 block mb-2 text-xs">
                Planogram Reference:
              </span>
              <div className="w-full overflow-hidden rounded border bg-white flex items-center justify-center">
                <img
                  src={item.productImageUri}
                  alt="POG Reference"
                  className="max-w-full max-h-48 object-contain"
                />
              </div>
            </div>
          ) : cropImage && cropCoordinates ? (
            <div>
              <span className="text-gray-600 block mb-2 text-xs">
                Detected Area:
              </span>
              <div
                className="w-full overflow-hidden rounded border cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() =>
                  cameraImageElement && setZoomedImage(cameraImageElement)
                }
              >
                <BoxedImage
                  image={cropImage}
                  crop={cropCoordinates}
                  cropPadding={20}
                  boxes={[
                    {
                      shape: cropCoordinates,
                      color: layerBboxColor || '#3b82f6',
                      strokeWidth: 5,
                    },
                  ]}
                />
              </div>
            </div>
          ) : (
            <div className="w-full h-48 flex items-center justify-center bg-gray-100 rounded border">
              <span className="text-gray-500 text-sm">No image available</span>
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="grid grid-cols-2 gap-2">
            {'x1' in item &&
              item.x1 != null &&
              item.y1 != null &&
              item.x2 != null &&
              item.y2 != null && (
                <>
                  <span className="text-gray-600">Coordinates:</span>
                  <span className="font-mono">
                    ({item.x1}, {item.y1}) - ({item.x2}, {item.y2})
                  </span>
                </>
              )}


            {item.rogItemUpc && (
              <>
                <span className="text-gray-600">UPC:</span>
                <span className="font-mono">{item.rogItemUpc}</span>
              </>
            )}

            {item.pogItemUpc && item.pogItemId && item.rogItemStatus !== RogItemStatus.REMOVE && (
              <>
                <span className="text-gray-600">Pog UPC:</span>
                <span className="font-mono">{item.pogItemUpc}</span>
              </>
            )}

            {item.rogPositionVector && item.rogPositionVector.tagName && (
              <>
                <span className="text-gray-600">Realogram:</span>
                <span className="font-mono whitespace-pre-line">
                  {`Mod: ${item.rogPositionVector.tagName}\nShelf: ${item.rogPositionVector.shelf}\nPosition: ${item.rogPositionVector.position}`}
                </span>
              </>
            )}

            {item.pogPositionVector && item.pogPositionVector.tagName && item.rogItemStatus !== RogItemStatus.REMOVE && (
              <>
                <span className="text-gray-600">Planogram:</span>
                <span className="font-mono whitespace-pre-line">
                  {`Mod: ${item.pogPositionVector.tagName}\nShelf: ${item.pogPositionVector.shelf}\nPosition: ${item.pogPositionVector.position}`}
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Zoom Modal */}
      {zoomedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={() => setZoomedImage(null)}
        >
          <div className="relative w-full h-full flex items-center justify-center">
            {zoomedImage === cameraImageElement && cropCoordinates ? (
              // Show camera image with detected box highlighted
              (() => {
                const maxWidth = window.innerWidth * 0.9;
                const maxHeight = window.innerHeight * 0.9;
                const imageAspectRatio =
                  zoomedImage.naturalWidth / zoomedImage.naturalHeight;
                const containerAspectRatio = maxWidth / maxHeight;

                let displayWidth, displayHeight;
                if (imageAspectRatio > containerAspectRatio) {
                  // Image is wider than container
                  displayWidth = Math.min(maxWidth, zoomedImage.naturalWidth);
                  displayHeight = displayWidth / imageAspectRatio;
                } else {
                  // Image is taller than container
                  displayHeight = Math.min(
                    maxHeight,
                    zoomedImage.naturalHeight
                  );
                  displayWidth = displayHeight * imageAspectRatio;
                }

                return (
                  <div
                    className="bg-white rounded shadow-lg"
                    style={{
                      width: `${displayWidth}px`,
                      height: `${displayHeight}px`,
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <BoxedImage
                      image={zoomedImage}
                      boxes={[
                        {
                          shape: cropCoordinates,
                          color: layerBboxColor || '#3b82f6',
                          strokeWidth: 10,
                        },
                      ]}
                    />
                  </div>
                );
              })()
            ) : (
              // Show regular product image
              <img
                src={zoomedImage.src}
                alt="Zoomed product"
                className="max-w-[90vw] max-h-[90vh] object-contain rounded shadow-lg"
                onClick={(e) => e.stopPropagation()}
              />
            )}
            <button
              onClick={() => setZoomedImage(null)}
              className="absolute top-4 right-4 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 transition-all shadow-lg"
            >
              <svg
                className="w-6 h-6 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
};
